import React from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LoginIcon from "@mui/icons-material/Login";
import EventIcon from "@mui/icons-material/Event";

/**
 * Panel de log de auditoría para Admin Dashboard
 */
export default function AuditLogPanel({ loading, auditLog, onGoAuditLog }) {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionColor = (action) => {
    if (action.includes("view")) return "info";
    if (action.includes("create") || action.includes("update")) return "success";
    if (action.includes("delete")) return "error";
    if (action.includes("login")) return "primary";
    return "default";
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Audit Log (24h)
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={onGoAuditLog}
          >
            View All
          </Button>
        </Stack>

        {/* Quick Stats */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <EventIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {auditLog.eventsLast24h.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Events
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <VisibilityIcon color="info" />
            <Typography variant="h6" fontWeight={600}>
              {auditLog.patientViews.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Patient Views
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <FileDownloadIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              {auditLog.dataExports}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Data Exports
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <LoginIcon color={auditLog.failedLogins > 5 ? "warning" : "success"} />
            <Typography variant="h6" fontWeight={600}>
              {auditLog.loginAttempts}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Login Attempts
            </Typography>
          </Box>
        </Box>

        {/* Recent Events */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Recent Activity
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLog.recentEvents?.slice(0, 5).map((event, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {formatTime(event.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {event.user}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={event.action.replace("_", " ")}
                      size="small"
                      color={getActionColor(event.action)}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
}
